import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { EmployeeService } from '../employee.service';
import { NgForm } from '@angular/forms';
import { ApiRequestService } from '../../shared/api-request.service';
import { TradeModel } from '../../admin/trades/trades.model';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-employees-details',
  templateUrl: './employee-details.component.html',
  styleUrls: ['./employee-details.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeeDetailsComponent implements OnInit {
  trades: TradeModel[] = [];

  // Mobile field Country Code selector values
  mobile_input_id: string = 'phone-1';
  mobile_number: string = '';
  mobile_country_code: string = '';
  mobile_dial_code: string = '';
  mobile_e164: string = '';
  mobile_error_state: boolean = false;

  // RBAC permission flag. Resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canEdit: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public employeeService: EmployeeService,
    public rbac: RBACService
  ) {}

  /**
   * Whether the employee is a partner user whose details are read-only to the current user.
   */
  get profileLocked(): boolean {
    return this.rbac.partnerLockApplies(this.employeeService.employee.is_partner_managed);
  }

  /**
   * Whether the current user may edit this employee's details (edit permission and no partner lock).
   */
  get canEditProfile(): boolean {
    return this.canEdit && !this.profileLocked;
  }

  ngOnInit() {
    // Resolve the RBAC permission flag once.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // Get Unique Names for Trades
    this.getTrades();
  }

  /**
   * Get Output values from phone input component.
   */
  getOutputPhoneDetails($event) {
    if ($event) {
      let phone = $event;
      this.mobile_number =
        phone.hasOwnProperty('number') && phone.number !== null
          ? phone.number
          : '';
      this.mobile_country_code =
        phone.hasOwnProperty('countryCode') && phone.countryCode !== null
          ? phone.countryCode
          : '';
      this.mobile_dial_code =
        phone.hasOwnProperty('dialCode') && phone.dialCode !== null
          ? phone.dialCode
          : '';
      this.mobile_e164 =
        phone.hasOwnProperty('e164Number') && phone.e164Number !== null
          ? phone.e164Number
          : '';
      this.mobile_error_state =
        phone.hasOwnProperty('errorState') && phone.errorState !== null
          ? phone.errorState
          : true;
    }
  }

  private async getTrades() {
    this.trades = await this.utils.getTrades();
  }

  onSubmit(form: NgForm) {
    // Partner users are managed by support.
    if (this.profileLocked) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Layer 2: RBAC guard for edit permission.
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit this employee.'
      );
      return;
    }

    if (!form.valid) {
      this.utils.showFormValidationError();
      return;
    }

    // Check Error states of phone input then continue submission
    if (this.mobile_error_state) {
      if (this.mobile_number.length < 3) {
        this.utils.showFormValidationError(
          'A Phone number must be at least 3 characters long.'
        );
      } else {
        this.utils.showFormValidationError();
      }
      return;
    }

    // Update User details with Mobile field Country Code selector values
    this.employeeService.employee.mobile = this.mobile_number;
    this.employeeService.employee.mobile_country_code = this.mobile_country_code;
    this.employeeService.employee.mobile_dial_code = this.mobile_dial_code;
    this.employeeService.employee.mobile_e164 = this.mobile_e164;

    this.employeeService.updateEmployee();
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}
